import { createFileRoute, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Search, Loader2, Download, X, Tag, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { listContacts } from "@/lib/contacts.functions";
import { formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import { ManualPushButton } from "@/components/manual-push-button";
import { EmptyState } from "@/components/empty-state";

export const Route = createFileRoute("/_authenticated/workspaces/$workspaceId/contacts")({
  component: ContactsView,
});

const ALL_SEGMENTS = [
  "active_paid",
  "active_trial",
  "at_risk_trial",
  "upgraded_paid",
  "churned_trial",
  "cancelled_trial",
  "converted_paid",
  "converted_then_cancelled",
  "at_risk_paid",
  "cancelled_paid",
  "second_tier_affiliate",
  "second_tier_commission",
];

function ContactsView() {
  const { workspaceId } = useParams({ from: "/_authenticated/workspaces/$workspaceId/contacts" });
  const fetchContacts = useServerFn(listContacts);

  const [search, setSearch] = useState("");
  const [segments, setSegments] = useState<string[]>([]);
  const [tier, setTier] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  type SortKey = "name" | "email" | "status" | "commission" | "date" | "segments";
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Three-state cycle per column: asc -> desc -> default (most recent date)
  const handleSort = (key: SortKey) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortKey(null);
  };
  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["contacts", workspaceId, search, from, to, tier],
    queryFn: () =>
      fetchContacts({
        data: {
          workspace_id: workspaceId,
          search: search || null,
          tier: tier || null,
          from: from ? new Date(from).toISOString() : null,
          to: to ? new Date(to + "T23:59:59").toISOString() : null,
          limit: 1000,
        },
      }),
  });

  const allRows = data?.contacts ?? [];

  const rows = useMemo(() => {
    const filtered = !segments.length
      ? allRows
      : allRows.filter((r: any) =>
          (r.segments ?? []).some((s: any) => segments.includes(s.segment_name)),
        );
    if (!sortKey) return filtered; // default: server-ordered by date_created desc
    const dir = sortDir === "asc" ? 1 : -1;
    const get = (r: any): string | number => {
      switch (sortKey) {
        case "name": return `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim().toLowerCase();
        case "email": return (r.email ?? "").toLowerCase();
        case "status": return (r.status ?? "").toLowerCase();
        case "commission": return Number(r.commission_amount ?? 0);
        case "date": return r.date_created ? new Date(r.date_created).getTime() : 0;
        case "segments": return ((r.segments ?? []).map((s: any) => s.segment_name).sort().join(",")).toLowerCase();
      }
    };
    return [...filtered].sort((a, b) => {
      const av = get(a), bv = get(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [allRows, segments, sortKey, sortDir]);

  const tiers = useMemo(() => {
    const set = new Set<string>();
    allRows.forEach((r: any) => r.tier && set.add(r.tier));
    return Array.from(set).sort();
  }, [allRows]);

  const allSelected = rows.length > 0 && rows.every((r: any) => selected.has(r.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r: any) => r.id)));
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleSegment = (s: string) => {
    setSegments((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const clearFilters = () => {
    setSearch(""); setSegments([]); setTier(""); setFrom(""); setTo("");
  };

  const exportSelected = () => {
    const selectedRows = rows.filter((r: any) => selected.has(r.id));
    const headers = ["email", "first_name", "last_name", "tier", "status", "commission_amount", "date_created", "segments"];
    const csv = [
      headers.join(","),
      ...selectedRows.map((r: any) => [
        r.email, r.first_name ?? "", r.last_name ?? "", r.tier ?? "", r.status ?? "",
        r.commission_amount ?? 0,
        r.date_created ?? "",
        (r.segments ?? []).map((s: any) => s.segment_name).join("|"),
      ].map((v) => {
        const s = String(v).replaceAll('"', '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      }).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `contacts-${selectedRows.length}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${selectedRows.length} contacts`);
  };

  const filtersActive = !!(search || segments.length || tier || from || to);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search email or name" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              Segments {segments.length > 0 && <Badge variant="secondary" className="ml-2">{segments.length}</Badge>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64" align="start">
            <div className="space-y-2">
              {ALL_SEGMENTS.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={segments.includes(s)} onCheckedChange={() => toggleSegment(s)} />
                  {s}
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              Tier {tier && <Badge variant="secondary" className="ml-2">{tier}</Badge>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56" align="start">
            <div className="space-y-1 max-h-64 overflow-y-auto">
              <button className="w-full text-left text-sm px-2 py-1 rounded hover:bg-accent" onClick={() => setTier("")}>All tiers</button>
              {tiers.map((t) => (
                <button key={t} className={`w-full text-left text-sm px-2 py-1 rounded hover:bg-accent ${tier === t ? "bg-accent" : ""}`} onClick={() => setTier(t)}>
                  {t}
                </button>
              ))}
              {tiers.length === 0 && <div className="text-xs text-muted-foreground px-2 py-1">No tier data</div>}
            </div>
          </PopoverContent>
        </Popover>

        <div className="flex items-center gap-1 text-sm">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[150px]" />
          <span className="text-muted-foreground">→</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[150px]" />
        </div>

        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="h-3 w-3 mr-1" /> Clear
          </Button>
        )}

        <div className="ml-auto text-sm text-muted-foreground">
          {rows.length} contact{rows.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* Bulk action toolbar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 rounded-lg border bg-muted/50 p-3">
          <div className="text-sm font-medium">{selected.size} selected</div>
          <Button variant="outline" size="sm" onClick={exportSelected}>
            <Download className="h-3 w-3 mr-1" /> Export CSV
          </Button>
          <ManualPushButton
            workspaceId={workspaceId}
            contactIds={Array.from(selected)}
            onDone={() => setSelected(new Set())}
          />
          <Button variant="outline" size="sm" disabled>
            <Tag className="h-3 w-3 mr-1" /> Tag (soon)
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} className="ml-auto">
            Clear selection
          </Button>
        </div>
      )}

      {error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
          Failed to load contacts: {(error as Error).message}
        </div>
      ) : isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          variant="filtered"
          title="No contacts match"
          description="Try adjusting your filters or search."
        />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} /></TableHead>
                <TableHead>
                  <button onClick={() => handleSort("name")} className="inline-flex items-center gap-1 hover:text-foreground">
                    Name <SortIcon k="name" />
                  </button>
                </TableHead>
                <TableHead>
                  <button onClick={() => handleSort("email")} className="inline-flex items-center gap-1 hover:text-foreground">
                    Email <SortIcon k="email" />
                  </button>
                </TableHead>
                <TableHead>
                  <button onClick={() => handleSort("status")} className="inline-flex items-center gap-1 hover:text-foreground">
                    Status <SortIcon k="status" />
                  </button>
                </TableHead>
                <TableHead>Tier</TableHead>
                <TableHead className="text-right">
                  <button onClick={() => handleSort("commission")} className="inline-flex items-center gap-1 hover:text-foreground ml-auto">
                    Commission <SortIcon k="commission" />
                  </button>
                </TableHead>
                <TableHead>
                  <button onClick={() => handleSort("date")} className="inline-flex items-center gap-1 hover:text-foreground">
                    Date created <SortIcon k="date" />
                  </button>
                </TableHead>
                <TableHead>
                  <button onClick={() => handleSort("segments")} className="inline-flex items-center gap-1 hover:text-foreground">
                    Segments <SortIcon k="segments" />
                  </button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c: any) => (
                <TableRow key={c.id} data-state={selected.has(c.id) ? "selected" : undefined}>
                  <TableCell><Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} /></TableCell>
                  <TableCell>{[c.first_name, c.last_name].filter(Boolean).join(" ") || ","}</TableCell>
                  <TableCell className="font-mono text-xs">{c.email}</TableCell>
                  <TableCell>{c.status ?? ","}</TableCell>
                  <TableCell>{c.tier ?? ","}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(c.commission_amount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {c.date_created ? new Date(c.date_created).toLocaleDateString() : ","}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {(c.segments ?? []).map((s: any) => (
                        <Badge key={s.segment_name} variant="secondary" className="text-xs">{s.segment_name}</Badge>
                      ))}
                      {c.raw_data_json?.billing_flag === "at_risk_payment_late" && (
                        <Badge className="text-xs bg-amber-500/15 text-amber-700 border-amber-500/40 hover:bg-amber-500/20" variant="outline">Payment Late</Badge>
                      )}
                      {c.raw_data_json?.billing_flag === "at_risk_payment_failed" && (
                        <Badge className="text-xs bg-red-500/15 text-red-700 border-red-500/40 hover:bg-red-500/20" variant="outline">Payment Failed</Badge>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
